import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserGroupSummaryDto } from '@monorepo/shared';

interface UserGroupsCellProps {
  groups: UserGroupSummaryDto[];
}

export const UserGroupsCell = ({ groups }: UserGroupsCellProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (groups.length === 0) {
    return <span className="text-sm text-gray-500">-</span>;
  }

  if (groups.length === 1) {
    return <span className="text-sm text-[#171a1c]">{groups[0].name}</span>;
  }

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="px-3 py-1 inline-flex text-xs leading-5 font-medium rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200 transition"
      >
        {t('admin.users.table.groups_count', { count: groups.length })}
      </button>
      {isOpen && (
        <div className="absolute left-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
          <ul className="py-1 max-h-60 overflow-auto">
            {groups.map((group) => (
              <li key={group.id} className="px-4 py-2 text-sm text-gray-700">
                {group.name}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
